import { Button, ButtonGroup, FormHelperText } from '@mui/material';
import { ArrowBackIosIcon, ArrowForwardIosIcon } from 'assets/icons';
import clsx from 'clsx';
import { format as formatDate, parse as parseDate } from 'date-fns';
import { mergeDeepRight } from 'ramda';
import React, { ReactElement } from 'react';
import { DateValue } from 'react-values';
import { TimeInput, TimeInputProps } from './TimeInput';
import { floorDate, steps } from './utils';

type OnChangeType = ({ value, previous }: { value: Date; previous: Date }) => void;

export interface TimePickerProps extends Omit<TimeInputProps, 'onChange' | 'error'> {
  error?: string;
  hoursOnly?: boolean;
  hoursStep?: number;
  input?: ReactElement;
  label?: string;
  minutesStep?: number;
  onChange?: OnChangeType;
  secondsStep?: number;
  value?: number | Date | string;
}

const noop = (): void => undefined;

export const defaultTimePickerProps = {
  hoursOnly: false,
  hoursStep: steps.hours,
  minutesStep: steps.minutes,
  onChange: noop,
  secondsStep: steps.seconds,
  format: (date: Date) => formatDate(date, 'HH:mm'),
  parse: (value: string) => parseDate(value, 'HH:mm', new Date()),
};

export const TimePicker: React.FC<TimePickerProps> = (props) => {
  const {
    error,
    hoursOnly,
    hoursStep,
    minutesStep,
    onChange,
    secondsStep,
    value = new Date(),
    format,
    parse,
    ...otherProps
  } = mergeDeepRight(defaultTimePickerProps, props);

  const round = floorDate({ hoursStep, minutesStep, secondsStep });
  const date = round(Number(value));

  return (
    <DateValue
      value={date}
      onChange={(nextValue: Date) =>
        onChange({
          value: nextValue,
          previous: date,
        })
      }
    >
      {({
        set: setValue,
        value: current,
        incrementHours,
        incrementMinutes,
        decrementHours,
        decrementMinutes,
      }: {
        set: (date: Date) => void;
        value: Date;
        incrementHours: (hours: number) => void;
        incrementMinutes: (minutes: number) => void;
        decrementHours: (hours: number) => void;
        decrementMinutes: (minutes: number) => void;
      }) => (
        <div className="flex flex-col w-full">
          <div className="flex">
            <ButtonGroup>
              <Button
                className={clsx('!min-w-0 p-0 h-12', props.disabled ? 'bg-grey-50' : 'bg-green-50', {
                  'rounded-r-none': hoursOnly,
                })}
                disabled={props.disabled}
                onClick={() => decrementHours(hoursStep)}
              >
                <ArrowBackIosIcon className={clsx('w-4 text-green-200', { 'text-grey-300': props.disabled })} />
              </Button>
              {!hoursOnly && (
                <Button
                  className={clsx('!min-w-0 p-0 h-12 rounded-r-none', props.disabled ? 'bg-grey-50' : 'bg-green-50')}
                  disabled={props.disabled}
                  onClick={() => decrementMinutes(minutesStep)}
                >
                  <ArrowBackIosIcon className={clsx('w-4 text-green-200', { 'text-grey-300': props.disabled })} />
                </Button>
              )}
            </ButtonGroup>
            <TimeInput
              aria-invalid={!!error}
              className={clsx('h-12 bg-white', { 'bg-error-500': error })}
              value={format(round(current))}
              onChange={(event, nextValue) => setValue(round(parse(nextValue)))}
              {...otherProps}
            />
            <ButtonGroup>
              {!hoursOnly && (
                <Button
                  className={clsx('!min-w-0 p-0 h-12 rounded-l-none', props.disabled ? 'bg-grey-50' : 'bg-green-50')}
                  disabled={props.disabled}
                  onClick={() => incrementMinutes(minutesStep)}
                >
                  <ArrowForwardIosIcon
                    className={clsx('w-4 text-green-200', { 'text-grey-300': props.disabled })}
                    data-testid="ArrowForwardIosIcon"
                  />
                </Button>
              )}
              <Button
                className={clsx('!min-w-0 p-0 h-12', props.disabled ? 'bg-grey-50' : 'bg-green-50', {
                  'rounded-l-none': hoursOnly,
                })}
                disabled={props.disabled}
                onClick={() => incrementHours(hoursStep)}
              >
                <ArrowForwardIosIcon
                  className={clsx('w-4 text-green-200', { 'text-grey-300': props.disabled })}
                  data-testid="ArrowForwardIosIcon"
                />
              </Button>
            </ButtonGroup>
          </div>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </div>
      )}
    </DateValue>
  );
};
